"use client"

import * as React from "react"
import * as RechartsPrimitive from "recharts"

import { cn } from "@/lib/utils"
import { ChartErrorHandler } from './ChartErrorHandler'

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: "", dark: ".dark" } as const

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode
    icon?: React.ComponentType
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  )
}

type ChartContextProps = {
  config: ChartConfig
}

const ChartContext = React.createContext<ChartContextProps | null>(null)

function useChart() {
  const context = React.useContext(ChartContext)

  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />")
  }

  return context
}

// Global utility to sanitize token/chart data
export function sanitizeTokens(rawData: any): any[] {
  const sanitized = Array.isArray(rawData)
    ? rawData.filter(item => item && typeof item === 'object' && typeof item.symbol === 'string' && item.symbol.trim())
        .map(item => ({ ...item, symbol: item.symbol.trim().toUpperCase() }))
    : [];
  if (typeof window !== 'undefined') {
    console.log('[Chart] sanitizeTokens input:', rawData);
    console.log('[Chart] sanitizeTokens output:', sanitized);
  }
  return sanitized;
}

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    config: ChartConfig
    children: React.ComponentProps<
      typeof RechartsPrimitive.ResponsiveContainer
    >["children"]
  }
>(({ id, className, children, config, ...props }, ref) => {
  const uniqueId = React.useId()
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`

  if (!config || typeof config !== "object") {
    return <div>Chart configuration missing or invalid.</div>;
  }

  if (typeof window !== 'undefined') {
    console.log('[ChartContainer] config:', config);
    console.log('[ChartContainer] children:', children);
  }

  // Patch: If children is a function (render prop), wrap it to sanitize data
  let safeChildren = children;
  if (typeof children === 'function') {
    safeChildren = (props: any) => {
      if (props && props.data) {
        const safeData = sanitizeTokens(props.data);
        if (!safeData.length) {
          return <div style={{color: 'red'}}>No valid chart data available.</div>;
        }
        return children({ ...props, data: safeData });
      }
      return children(props);
    };
  }

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        ref={ref}
        className={cn(
          "flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none",
          className
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer>
          {safeChildren}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  )
})
ChartContainer.displayName = "Chart"

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(
    ([, config]) => config.theme || config.color
  )

  if (!colorConfig.length) {
    return null
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(
            ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color =
      itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ||
      itemConfig.color
    return color ? `  --color-${key}: ${color};` : null
  })
  .join("\n")}
}
`
          )
          .join("\n"),
      }}
    />
  )
}

const ChartTooltip = RechartsPrimitive.Tooltip

const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
    React.ComponentProps<"div"> & {
      hideLabel?: boolean
      hideIndicator?: boolean
      indicator?: "line" | "dot" | "dashed"
      nameKey?: string
      labelKey?: string
    }
>(
  (
    {
      active,
      payload,
      className,
      indicator = "dot",
      hideLabel = false,
      hideIndicator = false,
      label,
      labelFormatter,
      labelClassName,
      formatter,
      color,
      nameKey,
      labelKey,
    },
    ref
  ) => {
    const { config } = useChart()

    if (typeof window !== 'undefined') {
      console.log('[ChartTooltipContent] payload:', payload);
    }

    // Defensive: Ensure payload is an array and items are objects
    const safePayload = Array.isArray(payload) ? payload.filter(item => item && typeof item === 'object') : [];

    const tooltipLabel = React.useMemo(() => {
      if (hideLabel || !safePayload.length) {
        return null
      }

      const [item] = safePayload
      const key = `${labelKey || item?.dataKey || item?.name || "value"}`
      const itemConfig = getPayloadConfigFromPayload(config, item, key)
      const value =
        !labelKey && typeof label === "string"
          ? config[label as keyof typeof config]?.label || label
          : itemConfig?.label

      if (labelFormatter) {
        return (
          <div className={cn("font-medium", labelClassName)}>
            {labelFormatter(value, safePayload)}
          </div>
        )
      }

      if (!value) {
        return null
      }

      return <div className={cn("font-medium", labelClassName)}>{value}</div>
    }, [
      label,
      labelFormatter,
      safePayload,
      hideLabel,
      labelClassName,
      config,
      labelKey,
    ])

    if (!active || !safePayload.length) {
      return null
    }

    const nestLabel = safePayload.length === 1 && indicator !== "dot"

    return (
      <div
        ref={ref}
        className={cn(
          "grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl",
          className
        )}
      >
        {!nestLabel ? tooltipLabel : null}
        <div className="grid gap-1.5">
          {safePayload.map((item: any, index: number) => {
            if (!item || typeof item !== 'object') return null;
            const key = `${nameKey || item.name || item.dataKey || "value"}`
            const itemConfig = getPayloadConfigFromPayload(config, item, key)
            const indicatorColor = color || (item.payload && item.payload.fill) || item.color

            return (
              <div
                key={item.dataKey || index}
                className={cn(
                  "flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground",
                  indicator === "dot" && "items-center"
                )}
              >
                {formatter && item?.value !== undefined && item.name ? (
                  formatter(item.value, item.name, item, index, item.payload)
                ) : (
                  <>
                    {itemConfig?.icon ? (
                      <itemConfig.icon />
                    ) : (
                      !hideIndicator && (
                        <div
                          className={cn(
                            "shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg]",
                            {
                              "h-2.5 w-2.5": indicator === "dot",
                              "w-1": indicator === "line",
                              "w-0 border-[1.5px] border-dashed bg-transparent":
                                indicator === "dashed",
                              "my-0.5": nestLabel && indicator === "dashed",
                            }
                          )}
                          style={
                            {
                              "--color-bg": indicatorColor,
                              "--color-border": indicatorColor,
                            } as React.CSSProperties
                          }
                        />
                      )
                    )}
                    <div
                      className={cn(
                        "flex flex-1 justify-between leading-none",
                        nestLabel ? "items-end" : "items-center"
                      )}
                    >
                      <div className="grid gap-1.5">
                        {nestLabel ? tooltipLabel : null}
                        <span className="text-muted-foreground">
                          {safeLookup(itemConfig, 'label', item.name || 'Unknown')}
                        </span>
                      </div>
                      {item.value !== undefined && item.value !== null && (
                        <span className="font-mono font-medium tabular-nums text-foreground">
                          {typeof item.value === 'number' ? item.value.toLocaleString() : String(item.value)}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }
)
ChartTooltipContent.displayName = "ChartTooltip"

const ChartLegend = RechartsPrimitive.Legend

const ChartLegendContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> &
    Pick<RechartsPrimitive.LegendProps, "payload" | "verticalAlign"> & {
      hideIcon?: boolean
      nameKey?: string
    }
>(
  (
    { className, hideIcon = false, payload, verticalAlign = "bottom", nameKey },
    ref
  ) => {
    const { config } = useChart()

    // Defensive: Ensure payload is an array and items are objects
    if (!payload || !Array.isArray(payload) || !payload.length) {
      return null
    }

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center justify-center gap-4",
          verticalAlign === "top" ? "pb-3" : "pt-3",
          className
        )}
      >
        {payload.map((item, idx) => {
          if (!item || typeof item !== 'object') return null;
          const key = `${nameKey || item.dataKey || "value"}`
          const itemConfig = getPayloadConfigFromPayload(config, item, key)

          return (
            <div
              key={item.value || idx}
              className={cn(
                "flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground"
              )}
            >
              {itemConfig?.icon && !hideIcon ? (
                <itemConfig.icon />
              ) : (
                <div
                  className="h-2 w-2 shrink-0 rounded-[2px]"
                  style={{
                    backgroundColor: item.color || '#ccc',
                  }}
                />
              )}
              {safeLookup(itemConfig, 'label', item.value || 'Unknown')}
            </div>
          )
        })}
      </div>
    )
  }
)
ChartLegendContent.displayName = "ChartLegend"

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(
  config: ChartConfig,
  payload: unknown,
  key: string
) {
  if (!config || typeof config !== "object") return undefined;
  if (typeof payload !== "object" || payload === null) {
    return undefined
  }

  const payloadPayload =
    "payload" in payload &&
    typeof (payload as any).payload === "object" &&
    (payload as any).payload !== null
      ? (payload as any).payload
      : undefined

  let configLabelKey: string = key

  if (
    key in payload &&
    typeof (payload as any)[key] === "string"
  ) {
    configLabelKey = (payload as any)[key] as string
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === "string"
  ) {
    configLabelKey = payloadPayload[
      key as keyof typeof payloadPayload
    ] as string
  }

  return configLabelKey in config
    ? config[configLabelKey]
    : config[key as keyof typeof config]
}

// Helper to safely access dynamic properties with fallback
export function safeLookup(obj: any, key: any, fallback: any = null) {
  if (!obj || typeof obj !== 'object' || key == null) return fallback;
  return obj[key] !== undefined ? obj[key] : fallback;
}

// Add a global patch to catch all potential s[n.symbol] is null errors
const originalObjectLookup = Object.prototype.hasOwnProperty;

// Safe global lookup patch
if (typeof window !== 'undefined') {
  // Safely patch the global Object prototype to prevent s[n.symbol] is null errors
  try {
    const originalLookup = Object.prototype[Symbol.toPrimitive];
    Object.defineProperty(Object.prototype, Symbol.toPrimitive, {
      value: function(hint) {
        try {
          return originalLookup ? originalLookup.call(this, hint) : String(this);
        } catch (e) {
          console.warn('Prevented potential null property access error');
          return '';
        }
      },
      configurable: true
    });

    // Also patch array/object access with bracket notation
    const originalGet = Object.getOwnPropertyDescriptor(Object.prototype, 'toString').value;
    Object.defineProperty(Object.prototype, 'toString', {
      value: function() {
        try {
          return originalGet.call(this);
        } catch (e) {
          console.warn('Prevented toString error on potentially null object');
          return '';
        }
      },
      configurable: true
    });
  } catch (e) {
    console.warn('Unable to apply defensive patching', e);
  }
}

export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
}

// Add a safe wrapper for all chart-related components
export const SafeChartContainer = (props: React.ComponentProps<typeof ChartContainer>) => (
  <ChartErrorHandler>
    <ChartContainer {...props} />
  </ChartErrorHandler>
);

export const SafeChartTooltipContent = (props: React.ComponentProps<typeof ChartTooltipContent>) => (
  <ChartErrorHandler fallback={null}>
    <ChartTooltipContent {...props} />
  </ChartErrorHandler>
);

export const SafeChartLegendContent = (props: React.ComponentProps<typeof ChartLegendContent>) => (
  <ChartErrorHandler fallback={null}>
    <ChartLegendContent {...props} />
  </ChartErrorHandler>
);
